import React, { useCallback, } from 'react';
import style from './ListingConfig.module.css';
import CustomIcon from '../Common/CustomIcon/CustomIcon.js';
import TextSmall from 'Vulture/Field/TextSmall.js';
import { useFlushableInput, } from 'Vulture/lib/FormFlush.js';
import Select from 'react-select';

import {
	buildTestData,
	removeWhitespace,
} from 'Vulture/TestData.js';

import {
	getSession,
} from 'Vulture/Session.js';

import {
	hasPermission,
} from 'Vulture/Permissions';

import ListingIntegrationModal from './ListingIntegrationModal.js';
import DeleteIntegrationListingModal from './DeleteIntegrationListingModal.js';
import {
	ListingColumnConfig,
} from './ListingColumnConfig.js';

import {
	providerListToModalContent,
} from './listingIntegrationModels.js';

import {
	crawlListingIntegration,
	deleteIntegrationListingConfig,
} from 'Vulture/Listing.js';

import {
	useListingConfig,
} from './ListingConfigContext.js';

export const ListingConfigTier = (props) => {
	const {
		findChildEntitiesForRender,
		app,
		accountUUID,
		settingMap,
		openNewListingModal,
		listingConfigUUID,
		listNumber,
		listingColumnTypeOptions,
		pendingChanges,
		tier = 1,
		arrayOptions,
	} = props;

	const {
		listingConfigMap,
		applyListingConfigCredentials,
		updateListingConfig,
		updateListingConfigLabel,
		deleteListingConfigTier,
		dropListingConfigSubtreeLocally,
		addListingColumn,
		deleteListingColumn,
	} = useListingConfig();

	const providerUUID = settingMap['listing_integration_provider'].uuid;
	const providerListUUID = settingMap['listing_integration_list'].uuid;
	const listingConfigEntry = listingConfigMap?.[listingConfigUUID];
	const isIntegration = !!listingConfigEntry?.[`setting.${providerUUID}`];

	const provider = listingConfigEntry?.[`setting.${providerUUID}`]?.string;
	const providerList = listingConfigEntry?.[`setting.${providerListUUID}`]?.string;

	const providerOptions = Object.keys(providerListToModalContent).map((key) => {
		return {
			label: providerListToModalContent[key].label,
			value: key,
		};
	});

	const selectedProviderOption = providerOptions.find((opt) => {
		return opt.value === provider;
	});

	const listingConfigName = listingConfigMap[listingConfigUUID]?.display_name || '';

	const listingIntegrationUsernameSettingUUID = settingMap['listing_integration_username'].uuid;
	const listingIntegrationPasswordSettingUUID = settingMap['listing_integration_password'].uuid;

	const hasSetCredentials = (
		!!listingConfigEntry?.[`setting.${listingIntegrationUsernameSettingUUID}`]?.string ||
		!!listingConfigEntry?.[`setting.${listingIntegrationPasswordSettingUUID}`]?.string ||
		!!listingConfigEntry?.provider_auth_code ||
		!!listingConfigEntry?.provider_key ||
		!!listingConfigEntry?.provider_map_identifier
	);

	// `pending` marks a tier whose create thunk is still queued: no row exists
	// yet to attach credentials to, and none for the integration delete
	// endpoint to remove.
	const isIntegrationSaved = !listingConfigEntry?.pending;

	const listingConfigData = listingConfigMap[listingConfigUUID] || {};

	let listingColumns = [];
	for (const [ m2Key, m2Data, ] of Object.entries(listingConfigData || {})) {
		if (m2Key.startsWith('listing_column.') && m2Data) {
			listingColumns.push({
				uuid: m2Key.split('.')[1],
				...m2Data,
			});
		}
	}

	listingColumns.sort((a, b) => {
		return a.order > b.order ? 1 : -1;
	});

	const listingColumnLabelOptions = listingColumns.map((listingColumn) => {
		return {
			value: listingColumn.uuid,
			label: listingColumn.display_name,
			order: listingColumn.order,
		};
	});

	// Label = column at order 1; derived every render so the dropdown
	// reconciles immediately on add / delete / rename / label-pick.
	const listingConfigLabel = listingColumnLabelOptions[0] || {
		label: 'No Columns',
		value: '',
	};

	let blockTierDelete = false;
	let listingColumnCount = listingColumns.length;
	let columnElms = [];

	const hasParent = !!listingConfigMap[listingConfigData.parentUUID];

	// Integration configs are deletable even when populated (DEV-11714) — the
	// dedicated endpoint deletes their listings too, behind a warning modal.
	if ((listingConfigData.subtree_locked || listingConfigData.locked) && !isIntegration) {
		blockTierDelete = true;
	}

	const styles = {
		control: (base) => {
			return {
				...base,
				maxHeight: 30,
			};
		},
		dropdownIndicator: (base) => {
			return {
				...base,
				paddingTop:    0,
				paddingBottom: 0,
			};
		},
		clearIndicator: (base) => {
			return {
				...base,
				paddingTop:    0,
				paddingBottom: 0,
			};
		},
		menu: (base) => {
			return {
				...base,
				top:      20,
				position: 'absolute',
			};
		},
	};

	listingColumns.forEach((listingColumn, i) => {
		let listingColumnUUID = listingColumn.uuid;

		// listing_column_type lives under listing_column_type.<typeName> with
		// the UUID nested in the value; read it from subVal.uuid, not the key.
		let listingColumnTypeUUID = null;
		for (const [ subKey, subVal, ] of Object.entries(listingColumn)) {
			if (subKey.startsWith('listing_column_type.') && subVal?.uuid) {
				listingColumnTypeUUID = subVal.uuid;
				break;
			}
		}
		if (!listingColumnTypeUUID) {
			return '';
		}

		// Find the value for react-select.
		let listingColumnTypeValue = null;
		for (let option of listingColumnTypeOptions) {
			if (option.value === listingColumnTypeUUID) {
				listingColumnTypeValue = option;
			}
		}

		let deleteColumnButton = (
			<div
				className={style.deleteIcon}
				onClick={(e) => {
					e.preventDefault();
					deleteListingColumn(listingConfigUUID, listingColumnUUID);
				}}
			>
				<CustomIcon icon='minus-circle' />
			</div>
		);

		let isTypeLocked = false;

		// Block listing_column deletion if the listing_column is owned by a listing or a listing_column_group.
		// Lock the listing_column type if the listing_column is owned by a listing or a listing_column_group.
		if (listingColumn.locked) {
			deleteColumnButton = null;
			isTypeLocked = true;
		}

		const listingColumnName = listingColumn.display_name;
		const nameLocked = !!listingColumnName && !deleteColumnButton;

		columnElms.push(
			<ListingColumnConfig
				key={listingColumnUUID}
				listingColumnUUID={listingColumnUUID}
				index={i}
				app={app}
				listingConfigUUID={listingConfigUUID}
				accountUUID={accountUUID}
				listingColumns={listingColumns}
				listingColumnTypeOptions={listingColumnTypeOptions}
				listingColumnTypeValue={listingColumnTypeValue}
				listingColumnTypeUUID={listingColumnTypeUUID}
				deleteColumnButton={deleteColumnButton}
				nameLocked={nameLocked}
				isTypeLocked={isTypeLocked}
				styles={styles}
				arrayOptions={arrayOptions}
			/>
		);
	});

	const childEntityList = findChildEntitiesForRender('listing_config', listingConfigUUID);

	const addColumn = () => {
		addListingColumn(listingConfigUUID, {
			listingColumnTypeUUID: listingColumnTypeOptions[0]?.value,
		});
	};

	const buttonElms = [];

	buttonElms.push(
		<button
			className={`button ${style.button}`}
			key={'data-btn'}
			onClick={(e) => {
				e.preventDefault();
				addColumn();
			}}
			data-testing-info={buildTestData('button--data')}
			data-test-id='listingConfigAddDataButton'
		>
			<CustomIcon icon='plus'/> Data
		</button>
	);

	// Only display the add tier button if we're at the lowest tier.
	if (childEntityList.length === 0) {
		buttonElms.push(
			<button
				className={`button ${style.button}`}
				key='add-tier-btn'
				onClick={(e) => {
					e.preventDefault();
					openNewListingModal('listing_config', listingConfigUUID);
				}}
				data-testing-info={buildTestData('button--tier')}
				data-test-id='listingConfigAddTierButton'
			>
				<CustomIcon icon='plus' /> Tier
			</button>
		);
	}

	if (isIntegration) {
		buttonElms.push(
			// Chrome and Safari suppress pointer events on a disabled control, so
			// the hint has to hang off a wrapper to be reachable on hover.
			<span
				key='set-credentials-btn'
				title={isIntegrationSaved ? undefined : 'Save the listing configuration before setting credentials'}
			>
				<button
					className={`button ${style.button} ${!isIntegrationSaved && 'button-disabled'}`}
					data-test-id='listingConfigSetCredentialsButton'
					onClick={(e) => {
						e.preventDefault();
						const providerLabel = providerListToModalContent[provider].label;
						const providerListCapitalized = providerList[0].toUpperCase() + providerList.slice(1);

						app.openModal({
							modalSize:  1,
							showModal:  true,
							modalProps: {
								title: `Log in ${providerLabel}: ${providerListCapitalized}`,
								jsx:   (
									<ListingIntegrationModal
										app={app}
										accountUUID={accountUUID}
										hasSetCredentials={hasSetCredentials}
										listingConfigUUID={listingConfigUUID}
										provider={provider}
										providerList={providerList}
										onCredentialsSet={(listingConfigFD) => {
											applyListingConfigCredentials(listingConfigUUID, listingConfigFD, [
												listingIntegrationUsernameSettingUUID,
												listingIntegrationPasswordSettingUUID,
												settingMap['listing_integration_mall_id']?.uuid,
											]);
										}}
										closeModal={() => {
											app.closeModal();
										}}
									/>
								),
							},
						});
					}}
					data-testing-info={buildTestData('set-credentials-button--tier')}
					disabled={!isIntegrationSaved}
				>
					Set credentials
				</button>
			</span>
		);

		const showCrawlStatusModal = (title, message) => {
			app.openModal({
				modalSize:  1,
				showModal:  true,
				modalProps: {
					title,
					jsx: (
						<div>
							<p data-testing-info={buildTestData('crawl-status-message')}>{message}</p>
							<button
								className='button'
								onClick={(e) => {
									e.preventDefault();
									app.closeModal();
								}}
								data-testing-info={buildTestData('button--crawlStatusOk')}
							>
								OK
							</button>
						</div>
					),
				},
			});
		};

		// Mirrors the backend gate on the crawl/generate endpoints, which moved to
		// `programming` (seeded to internal_admin alone).
		if (hasPermission('programming')) {
			buttonElms.push(
				<button
					className={`button ${style.button} ${!hasSetCredentials && 'button-disabled'}`}
					key='crawl-btn'
					onClick={async (e) => {
						e.preventDefault();

						app.setState({
							loading: true,
						});

						try {
							await crawlListingIntegration(accountUUID, listingConfigUUID, provider);

							showCrawlStatusModal('Crawl started', 'The crawl was started successfully. Listings will appear once it completes.');
						} catch (error) {
							console.error(error);
							showCrawlStatusModal('Crawl failed', `The crawl could not be started: ${error.message}`);
						} finally {
							app.setState({
								loading: false,
							});
						}
					}}
					data-testing-info={buildTestData('crawl-button--tier')}
					disabled={!hasSetCredentials}
				>
					Crawl
				</button>
			);
		}
	}

	const schedulingSettingUUID = settingMap?.['listing_scheduling_toggle']?.uuid;
	const isSchedulingChecked = !!listingConfigData?.[`setting.${schedulingSettingUUID}`]?.bool;

	let schedulingToggle = null;
	if (!isIntegration) {
		schedulingToggle = (
			<div className={style.listingConfigDataRowInput}>
				<div className={style.schedulingHeader} >
					Scheduling
				</div>
				<div className={style.checkbox}>
					<div
						className={isSchedulingChecked ? style.activeCheckbox : style.disabledSquare}
						onClick={(e) => {
							e.preventDefault();
							updateListingConfig(listingConfigUUID, {
								[`setting.${schedulingSettingUUID}`]: {
									bool: !isSchedulingChecked,
								},
							});
						}}>
						<div className={style.checkboxIcon}>
							{isSchedulingChecked && <CustomIcon icon="check"/>}
						</div>
					</div>
				</div>
			</div>
		);
	}

	// Assign All checkbox - only shown on tier 1 (root) configs
	const isAssignAllChecked = !!listingConfigData?.use_all_listings;

	// Check if use_all_listings is inherited from a parent config
	const isAssignAllInherited = (() => {
		let parentUUID = listingConfigData.parentUUID;
		while (parentUUID && listingConfigMap[parentUUID]) {
			if (listingConfigMap[parentUUID]?.use_all_listings) {
				return true;
			}
			parentUUID = listingConfigMap[parentUUID]?.parentUUID;
		}
		return false;
	})();

	// Integrations can't toggle it, but court-docket integrations (uscourts / kingcounty)
	// are registered with it ON (DEV-12184) — show that state read-only so the
	// "Every System at This Property" chips in the listings table are explained.
	const isAssignAllReadOnly = isAssignAllInherited || isIntegration;

	let assignAllToggle = null;
	if (tier === 1 && (!isIntegration || isAssignAllChecked)) {
		assignAllToggle = (
			<div className={style.listingConfigDataRowInput}>
				<div className={style.schedulingHeader}>All Listings</div>
				<div className={style.checkbox}>
					<div
						className={(isAssignAllChecked || isAssignAllInherited) ? style.activeCheckbox : style.disabledSquare}
						title={isIntegration ? 'Set automatically for this integration' : undefined}
						onClick={(e) => {
							e.preventDefault();
							if (isAssignAllReadOnly) {
								return; // Can't toggle if inherited or integration-managed
							}
							updateListingConfig(listingConfigUUID, {
								use_all_listings: !isAssignAllChecked,
							});
						}}>
						<div className={style.checkboxIcon}>
							{(isAssignAllChecked || isAssignAllInherited) && <CustomIcon icon="check"/>}
						</div>
					</div>
				</div>
			</div>
		);
	}

	const collectChildTierNames = (configUUID, names = []) => {
		for (const childUUID of listingConfigMap[configUUID]?.childTiers || []) {
			names.push(listingConfigMap[childUUID]?.display_name || 'Unnamed tier');
			collectChildTierNames(childUUID, names);
		}

		return names;
	};

	const collectSubtreeListingCount = (configUUID) => {
		let count = listingConfigMap[configUUID]?.listing_count || 0;

		for (const childUUID of listingConfigMap[configUUID]?.childTiers || []) {
			count += collectSubtreeListingCount(childUUID);
		}

		return count;
	};

	const openDeleteIntegrationModal = () => {
		const configName = listingConfigName || `List ${listNumber}`;

		app.openModal({
			modalSize:  2,
			showModal:  true,
			modalProps: {
				title: `Delete Integration List: ${configName}`,
				jsx:   (
					<DeleteIntegrationListingModal
						listingConfigName={configName}
						listingCount={collectSubtreeListingCount(listingConfigUUID)}
						childTierNames={collectChildTierNames(listingConfigUUID)}
						onConfirm={async () => {
							try {
								await deleteIntegrationListingConfig(listingConfigUUID);
							} catch (error) {
								console.error('Error deleting integration listing config:', error);
								const serverMessage = typeof error?.response?.data === 'string' ? error.response.data : '';
								alert(serverMessage || 'Failed to delete the integration list configuration. Please try again or contact support.');
								app.closeModal();

								return;
							}

							// The rows are gone server-side, so a local reconciliation
							// problem must not surface as a failed delete.
							try {
								// Purge unsaved edits referencing the deleted subtree so a
								// later Save can't post them against soft-deleted configs.
								const subtreeUUIDs = [ listingConfigUUID, ];
								const collectSubtreeUUIDs = (configUUID) => {
									for (const childUUID of listingConfigMap[configUUID]?.childTiers || []) {
										subtreeUUIDs.push(childUUID);
										collectSubtreeUUIDs(childUUID);
									}
								};
								collectSubtreeUUIDs(listingConfigUUID);

								const stalePendingKeys = Object.keys(pendingChanges || {}).filter((pendingKey) => {
									return subtreeUUIDs.some((subtreeUUID) => {
										return pendingKey.includes(subtreeUUID);
									});
								});

								app.delPendingChange(stalePendingKeys);
								app.closeModal();
								dropListingConfigSubtreeLocally(listingConfigUUID);
								app.fetchAccount(accountUUID);
							} catch (error) {
								console.error('Integration list deleted, but reconciling local state failed:', error);
								app.closeModal();
							}
						}}
						closeModal={() => {
							app.closeModal();
						}}
					/>
				),
			},
		});
	};

	let deleteTierButton = null;

	if (!blockTierDelete) {
		deleteTierButton = (
			<div
				className={style.deleteIcon}
				key='delete-btn'
				data-test-id='listingConfigTierDeleteIcon'
				onClick={(e) => {
					e.preventDefault();

					// Unsaved integration configs have no DB rows yet — those fall
					// through to the local pending-changes delete below.
					if (isIntegration && isIntegrationSaved) {
						openDeleteIntegrationModal();
						return;
					}

					const parentDisplayName = listingConfigMap[listingConfigData.parentUUID]?.display_name;
					const confirmation = confirm(
						parentDisplayName
							? `Are you sure you want to delete "${listingConfigName}" from "${parentDisplayName}"?`
							: `Are you sure you want to delete "${listingConfigName}"?`
					);

					if (!confirmation) {
						return;
					}

					deleteListingConfigTier(listingConfigUUID);
				}}
			>
				<CustomIcon icon='minus-circle' />
			</div>
		);
	}

	const handleTierNameBlur = useCallback((value) => {
		updateListingConfig(listingConfigUUID, { display_name: value, });
	}, [ updateListingConfig, listingConfigUUID, ]);
	const tierNameInputRef = useFlushableInput(handleTierNameBlur);

	const textSmall = (
		<TextSmall
			defaultValue={listingConfigName}
			onBlurCB={handleTierNameBlur}
			inputRef={tierNameInputRef}
			dataTestingInfo={buildTestData(`input--${removeWhitespace(listingConfigName)}`)}
			dataTestId='listingConfigTierNameInput'
		/>
	);

	return (
		<>
			<div
				className={style.listingConfigContainer}
				key={listingConfigUUID}
				data-testing-info={buildTestData(`list--list${listNumber}--tier${tier}`)}
				data-test-id={`listingConfigTier-list${listNumber}-tier${tier}`}
			>
				<div className={style.listingConfigLabel}>
					<h1>{listingConfigName || `List ${listNumber}`} {deleteTierButton}</h1>
				</div>
				<div className={style.listingConfigCode}>
					{listingConfigUUID}
				</div>

				<div className={style.listingConfigData}>
					<div className={style.listingConfigDataRow}>
						<div className={style.listingConfigDataRowInput}>
							<div>List Name</div>
							{isIntegration && textSmall}
							{!isIntegration && (
								<div className={style.crowForm}>{textSmall}</div>
							)}
						</div>
						{isIntegration && (
							<div className={style.listingConfigDataRowInput}>
								<div>Integration:</div>
								<div className={style.listingConfigSubcontainer}>
									<span className={style.integrationProviderText}>
										{selectedProviderOption ? selectedProviderOption.label : provider}
									</span>
								</div>
							</div>
						)}
						{!isIntegration && (
							<div className={style.toggleContainer}>
								{schedulingToggle}
								{assignAllToggle}
							</div>
						)}
					</div>
					{listingColumnLabelOptions.length > 0 &&
						<div className={style.listingConfigDataRow}>
							<div className={style.listingConfigDataRowInput}>
								<div style={{
									display:    'flex',
									alignItems: 'center',
									gap:        '4px',
								}}>
									<span>Label</span>
									<div className={style.settingHelpIcon}>
										<CustomIcon icon='question-circle' />
										<div className={style.settingHelpToolTip} style={{
											zIndex: 1,
										}}>
											{'The column shown as the primary label for each listing in this tier. Changing the Label reorders the columns so the picked one renders first.'}
										</div>
									</div>
								</div>
								<Select
									className={style.listingConfigSelectLabel}
									options={listingColumnLabelOptions}
									styles={styles}
									onChange={(e) => {
										updateListingConfigLabel(listingConfigUUID, e.value);
									}}
									value={listingConfigLabel}
								/>
							</div>
						</div>
					}

					{columnElms}
					<div className={style.buttonContainer}>
						{buttonElms}
					</div>
				</div>
			</div>

			{!props.hideChildRendering && childEntityList
				.map((childListingConfig) => {
					return (
						<ListingConfigTier
							{...props}
							key={childListingConfig.uuid}
							listingConfigUUID={childListingConfig.uuid}
							tier={tier + 1}
						/>
					);
				})}
		</>
	);
};